import { useEffect, useMemo, useRef, useState, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { AvailabilityPicker } from '@/components/appointments/AvailabilityPicker';
import { CourseTemplateTypeahead } from '@/components/appointments/CourseTemplateTypeahead';
import { PractitionerTypeahead } from '@/components/appointments/PractitionerTypeahead';
import { SlideOver } from '@/components/ui/SlideOver';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { DanishDateField } from '@/components/ui/DanishDateField';
import { TimeInput } from '@/components/ui/TimeInput';
import { Field } from '@/components/ui/Field';
import { Checkbox } from '@/components/ui/Checkbox';
import { Banner } from '@/components/ui/Banner';
import { Avatar } from '@/components/ui/Avatar';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import {
  usePatientPicker,
  usePatientsByIds,
  type PatientPickerItem,
} from '@/hooks/usePatientPicker';
import { useClinicMembers } from '@/hooks/useClinicMembers';
import { useClinicRoles } from '@/hooks/useClinicRoles';
import { usePatientStaffAssignments } from '@/hooks/usePatientStaffAssignments';
import { filterPractitionerMembers, withStickySelectedMembers } from '@/lib/practitionerRole';
import {
  practitionerSelectionAfterSuggestion,
  suggestedLinkedPractitionerIds,
} from '@/lib/practitionerSuggestions';
import { useServicesList } from '@/hooks/useServices';
import { bookableCourseTemplates } from '@/lib/appointmentTypeCatalog';
import { useRecentCourseSelections } from '@/hooks/useRecentCourseSelections';
import { useTreatmentCourseTemplates } from '@/hooks/useTreatmentCourseTemplates';
import {
  useBookTreatmentCourse,
  useEligiblePractitionersForTemplateStep,
} from '@/hooks/useBookTreatmentCourse';
import { formatPatientTelephoneNumbers } from '@/lib/patientStamkortFields';
import { parseBookedTreatmentCourse } from '@/lib/bookedTreatmentCourse';
import {
  applyManualCourseStepTime,
  bookTreatmentCoursePayload,
  canAdvanceFromPhase,
  canLeaveStepUnbooked,
  clinicWallFromIso,
  courseDraftReadyToBook,
  courseDraftWithoutAppointments,
  courseStepEndClock,
  defaultStepBookings,
  markAllStepsUnbooked,
  markStepUnbooked,
  nextPhase,
  previousPhase,
  showCreateWithoutAppointmentButton,
  wizardPhases,
  type DraftStepBooking,
  type WizardPhase,
} from '@/lib/treatmentCourseWizard';
import { isCompleteCivilDate } from '@/utils/appointmentCivilDate';
import { zonedDateTime, zonedYmd } from '@/lib/reporting/timezone';
import type {
  TreatmentCourseTemplateDraft,
  TreatmentCourseTemplateStepDraft,
} from '@/types/treatmentCourses';
import { formatAppointmentClock } from '@/utils/appointmentTimeFormat';

function courseChoiceDetail(
  row: TreatmentCourseTemplateDraft,
  translate: (key: string, options?: { count: number }) => string,
): string {
  const timing =
    row.booking_timing === 'allow_unbooked'
      ? translate('treatment_courses.booking_timing_allow_unbooked')
      : translate('treatment_courses.booking_timing_all_upfront');
  return `${translate('treatment_courses.step_count', { count: row.steps.length })} · ${timing}`;
}

function stepDisplayName(
  step: TreatmentCourseTemplateStepDraft,
  serviceName: string | undefined,
  fallback: string,
): string {
  const title = step.title.trim();
  if (title) return title;
  if (serviceName) return serviceName;
  return fallback;
}

function formatNames(ids: string[], names: Map<string, string>, empty: string): string {
  if (ids.length === 0) return empty;
  return ids.map((id) => names.get(id) ?? id).join(', ');
}

export type CreatedTreatmentCourse = {
  id: string;
  patientId: string;
};

export function BookTreatmentCourseSlideOver({
  clinicId,
  initialPatientId,
  onClose,
  onCreated,
}: {
  clinicId: string;
  initialPatientId?: string | null;
  onClose: () => void;
  /** Opens the new course. Called for both booked courses and «Opret uden aftale». */
  onCreated?: (course: CreatedTreatmentCourse) => void;
}) {
  const { t, i18n } = useTranslation();
  const [phase, setPhase] = useState<WizardPhase>(
    initialPatientId ? { type: 'template' } : { type: 'patient' },
  );
  const [patient, setPatient] = useState<PatientPickerItem | null>(null);
  const [template, setTemplate] = useState<TreatmentCourseTemplateDraft | null>(null);
  const [bookings, setBookings] = useState<DraftStepBooking[]>([]);
  const [selectedPractitioners, setSelectedPractitioners] = useState<string[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showSlots, setShowSlots] = useState(false);
  const [draftCivil, setDraftCivil] = useState('');
  const [draftStart, setDraftStart] = useState('');
  const [draftEnd, setDraftEnd] = useState('');
  const practitionerTouch = useRef(false);

  const { data: initialPatients } = usePatientsByIds(
    clinicId,
    initialPatientId ? [initialPatientId] : [],
  );
  const { data: templates, isLoading: templatesLoading } = useTreatmentCourseTemplates(clinicId, {
    includeInactive: false,
  });
  const { recentIds, remember } = useRecentCourseSelections(clinicId);
  const { data: members } = useClinicMembers(clinicId);
  const { data: roles, isSuccess: rolesReady } = useClinicRoles(clinicId);
  const assignments = usePatientStaffAssignments(patient?.id);
  const { data: services } = useServicesList(clinicId, { includeInactive: true });
  const bookCourse = useBookTreatmentCourse(clinicId);

  useEffect(() => {
    const initialPatient = initialPatients?.[0];
    if (!initialPatient) return;
    setPatient((current) => (current?.id === initialPatient.id ? current : initialPatient));
  }, [initialPatients]);

  const activeTemplates = bookableCourseTemplates(templates);
  const phases = wizardPhases(template?.steps.length ?? 0);
  const currentStep =
    phase.type === 'step' && template ? (template.steps[phase.index] ?? null) : null;
  const currentBooking = phase.type === 'step' ? (bookings[phase.index] ?? null) : null;
  const previousBooking =
    phase.type === 'step' && phase.index > 0 ? (bookings[phase.index - 1] ?? null) : null;

  const memberNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const member of members ?? []) {
      map.set(member.user_id, member.name || member.email);
    }
    return map;
  }, [members]);

  const serviceNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const service of services ?? []) {
      map.set(service.id, service.name);
    }
    return map;
  }, [services]);

  const {
    data: eligibleFromStep,
    isSuccess: eligibleSettled,
    isError: eligibleFailed,
  } = useEligiblePractitionersForTemplateStep(currentStep?.clientId ?? null, phase.type === 'step');
  const eligiblePractitionerIds = useMemo(() => {
    if (!currentStep) return [];
    const all = members ?? [];
    if (eligibleFromStep == null) {
      if (!rolesReady) return all.map((member) => member.user_id);
      return withStickySelectedMembers(
        filterPractitionerMembers(all, roles ?? []),
        all,
        selectedPractitioners,
      ).map((member) => member.user_id);
    }
    return eligibleFromStep;
  }, [currentStep, eligibleFromStep, members, roles, rolesReady, selectedPractitioners]);

  const behandlerIds = useMemo(() => {
    if (!rolesReady) return new Set<string>();
    return new Set(
      filterPractitionerMembers(members ?? [], roles ?? []).map((member) => member.user_id),
    );
  }, [members, roles, rolesReady]);
  const eligibilityPending = phase.type === 'step' && !eligibleSettled && !eligibleFailed;
  const suggestedPractitionerIds = useMemo(() => {
    if (!patient?.id || !rolesReady || assignments.isLoading || eligibilityPending) return [];
    return suggestedLinkedPractitionerIds({
      assignmentUserIds: (assignments.data ?? []).map((row) => row.user_id),
      behandlerIds,
      selectableIds:
        phase.type === 'step' && eligibleFromStep != null ? new Set(eligibleFromStep) : null,
    });
  }, [
    assignments.data,
    assignments.isLoading,
    behandlerIds,
    eligibilityPending,
    eligibleFromStep,
    patient?.id,
    phase.type,
    rolesReady,
  ]);
  const practitionerPeople = useMemo(() => {
    if (!currentStep) return [];
    // Until roles are known, an unrestricted step must not offer non-behandlere.
    if (eligibleFromStep == null && !rolesReady) return [];
    return eligiblePractitionerIds.map((id) => ({
      id,
      name: memberNames.get(id) ?? id,
    }));
  }, [currentStep, eligibleFromStep, eligiblePractitionerIds, memberNames, rolesReady]);
  const availabilityMembers = useMemo(() => {
    const allowed = new Set(eligiblePractitionerIds);
    return (members ?? []).filter((member) => allowed.has(member.user_id));
  }, [eligiblePractitionerIds, members]);

  useEffect(() => {
    if (practitionerTouch.current) return;
    if (phase.type !== 'step' || !patient?.id || !rolesReady || assignments.isLoading) return;
    if (eligibilityPending) return;
    const next = practitionerSelectionAfterSuggestion({
      currentIds: selectedPractitioners,
      suggestedIds: suggestedPractitionerIds,
      userAdjusted: false,
    });
    if (!next) return;
    setSelectedPractitioners(next);
    if (phase.type !== 'step') return;
    const index = phase.index;
    setBookings((rows) => {
      const row = rows[index];
      if (!row || row.timeSource !== 'manual' || !row.startsAt || row.practitionerIds.length > 0) {
        return rows;
      }
      return rows.map((item, itemIndex) =>
        itemIndex === index ? { ...item, practitionerIds: [...next] } : item,
      );
    });
  }, [
    assignments.isLoading,
    eligibilityPending,
    patient?.id,
    phase,
    rolesReady,
    selectedPractitioners,
    suggestedPractitionerIds,
  ]);

  const stepIndex = phase.type === 'step' ? phase.index : -1;
  const bookedStart = currentBooking?.startsAt ?? '';
  const bookedEnd = currentBooking?.endsAt ?? '';
  const stepDuration = currentStep?.duration_minutes ?? currentBooking?.durationMinutes ?? 0;
  const availabilityDuration = stepDuration > 0 ? stepDuration : 30;
  const selectedSlotTime = useMemo(() => {
    if (!bookedStart) return null;
    const date = new Date(bookedStart);
    return Number.isNaN(date.getTime()) ? null : date;
  }, [bookedStart]);

  useEffect(() => {
    setShowSlots(false);
  }, [stepIndex]);

  useEffect(() => {
    const start = clinicWallFromIso(bookedStart);
    const end = clinicWallFromIso(bookedEnd);
    setDraftCivil(start.civil);
    setDraftStart(start.clock);
    setDraftEnd(end.clock);
  }, [bookedEnd, bookedStart, stepIndex]);

  const dirty = Boolean(
    patient || template || bookings.some((row) => row.startsAt || row.unbooked),
  );
  const canContinue = canAdvanceFromPhase({
    phase,
    patientId: patient?.id ?? null,
    template,
    bookings,
  });
  const draftInput = {
    patientId: patient?.id ?? null,
    template,
    bookings,
  };
  const withoutAppointments = courseDraftWithoutAppointments(draftInput);
  const canBook = courseDraftReadyToBook(draftInput);

  function selectTemplate(next: TreatmentCourseTemplateDraft | null) {
    setTemplate(next);
    setBookings(next ? defaultStepBookings(next) : []);
    practitionerTouch.current = false;
    setSelectedPractitioners([]);
    if (next?.id) remember(next.id);
  }

  function patchStepBooking(index: number, patch: (row: DraftStepBooking) => DraftStepBooking) {
    setBookings((rows) => {
      const current = rows[index];
      if (!current) return rows;
      const next = patch(current);
      if (next === current) return rows;
      return rows.map((row, rowIndex) => (rowIndex === index ? next : row));
    });
  }

  function choosePractitioners(ids: string[]) {
    practitionerTouch.current = true;
    setSelectedPractitioners(ids);
    if (phase.type !== 'step') return;
    const index = phase.index;
    patchStepBooking(index, (row) => {
      if (row.timeSource !== 'manual' || !row.startsAt) return row;
      if (row.practitionerIds.join() === ids.join()) return row;
      return { ...row, practitionerIds: [...ids], resourceIds: [] };
    });
  }

  function writeManualTime(input: {
    civil: string;
    startClock: string;
    endClock?: string;
    durationMinutes?: number;
    practitionerIds?: readonly string[];
  }) {
    if (phase.type !== 'step') return;
    const index = phase.index;
    const practitionerIds = input.practitionerIds ?? selectedPractitioners;
    patchStepBooking(index, (row) =>
      applyManualCourseStepTime(row, {
        civil: input.civil,
        startClock: input.startClock,
        endClock: input.endClock,
        durationMinutes: input.durationMinutes,
        practitionerIds,
      }),
    );
  }

  function onAvailabilitySelect(dateTime: Date, clinicianId: string | null) {
    const parts = zonedDateTime(dateTime);
    const pad = (n: number) => String(n).padStart(2, '0');
    const civil = `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
    const startClock = `${pad(parts.hour)}:${pad(parts.minute)}`;
    const practitionerIds = clinicianId ? [clinicianId] : selectedPractitioners;
    if (clinicianId && selectedPractitioners.join() !== clinicianId) {
      practitionerTouch.current = true;
      setSelectedPractitioners([clinicianId]);
    }
    writeManualTime({
      civil,
      startClock,
      durationMinutes: availabilityDuration,
      practitionerIds,
    });
  }

  function clearStepTime() {
    if (phase.type !== 'step') return;
    const index = phase.index;
    patchStepBooking(index, (row) =>
      row.startsAt
        ? { ...row, startsAt: null, endsAt: null, resourceIds: [], timeSource: undefined }
        : row,
    );
  }

  function onDraftDate(civil: string) {
    if (civil !== '' && !isCompleteCivilDate(civil)) return;
    setDraftCivil(civil);
    if (!civil || !draftStart) {
      if (!civil) clearStepTime();
      return;
    }
    const end = courseStepEndClock(civil, draftStart, stepDuration) ?? '';
    setDraftEnd(end);
    writeManualTime({ civil, startClock: draftStart, durationMinutes: stepDuration });
  }

  function onDraftStart(clock: string) {
    setDraftStart(clock);
    if (!clock) {
      setDraftEnd('');
      clearStepTime();
      return;
    }
    const civil = draftCivil || zonedYmd(new Date());
    if (!draftCivil) setDraftCivil(civil);
    const end = courseStepEndClock(civil, clock, stepDuration) ?? '';
    setDraftEnd(end);
    writeManualTime({ civil, startClock: clock, durationMinutes: stepDuration });
  }

  function onDraftEnd(clock: string) {
    setDraftEnd(clock);
    const civil = draftCivil || zonedYmd(new Date());
    if (!draftStart || !clock) return;
    if (!draftCivil) setDraftCivil(civil);
    writeManualTime({ civil, startClock: draftStart, endClock: clock });
  }

  function goNext() {
    const next = nextPhase(phases, phase);
    if (next) setPhase(next);
  }

  function goBack() {
    if (phase.type === 'review' && withoutAppointments && template) {
      setBookings(defaultStepBookings(template));
      setPhase({ type: 'step', index: 0 });
      return;
    }
    const prev = previousPhase(phases, phase);
    if (prev) setPhase(prev);
    else onClose();
  }

  function startWithoutAppointments() {
    setBookings((rows) => markAllStepsUnbooked(rows));
    setPhase({ type: 'review' });
  }

  async function submitCourse() {
    if (!template?.id || !patient || !canBook) return;
    setSubmitError(null);
    try {
      const createdRaw = await bookCourse.mutateAsync({
        patientId: patient.id,
        templateId: template.id,
        steps: bookTreatmentCoursePayload({ template, bookings }),
      });
      const created = parseBookedTreatmentCourse(createdRaw);
      toast.success(
        withoutAppointments
          ? t('treatment_courses.book_toast_created_without')
          : t('treatment_courses.book_toast_created'),
      );
      if (created?.id && created.patient_id) {
        onCreated?.({ id: created.id, patientId: created.patient_id });
      }
      onClose();
    } catch (err) {
      console.error('book treatment course failed', err);
      setSubmitError(t('treatment_courses.book_toast_error'));
    }
  }

  const title =
    phase.type === 'step' && currentStep
      ? t('treatment_courses.book_choose_time', {
          n: phase.index + 1,
          name: stepDisplayName(
            currentStep,
            serviceNames.get(currentStep.service_id),
            t('treatment_courses.step_no_service'),
          ),
        })
      : t('treatment_courses.book_title');

  return (
    <SlideOver
      open
      wide
      title={title}
      description={
        phase.type === 'review'
          ? withoutAppointments
            ? t('treatment_courses.book_review_subtitle_without')
            : t('treatment_courses.book_review_subtitle')
          : t('treatment_courses.book_subtitle')
      }
      onClose={onClose}
      dirty={dirty}
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-s2">
          <div>
            {showCreateWithoutAppointmentButton(
              phase,
              template?.booking_timing ?? 'all_upfront',
            ) ? (
              <Button type="button" variant="secondary" onClick={startWithoutAppointments}>
                {t('treatment_courses.book_create_without_appointment')}
              </Button>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-s2">
            <Button type="button" variant="secondary" onClick={goBack}>
              {phase.type === 'patient' ? t('common.close') : t('treatment_courses.book_back')}
            </Button>
            {phase.type === 'review' ? (
              <Button
                type="button"
                onClick={() => void submitCourse()}
                disabled={!canBook || bookCourse.isPending}
              >
                {withoutAppointments
                  ? t('treatment_courses.book_create_submit')
                  : t('treatment_courses.book_submit')}
              </Button>
            ) : (
              <Button type="button" onClick={goNext} disabled={!canContinue}>
                {t('treatment_courses.book_continue')}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-s4">
        {submitError ? <Banner tone="error" title={submitError} /> : null}

        {phase.type === 'patient' ? (
          <CoursePatientPicker
            clinicId={clinicId}
            value={patient}
            onChange={(next) => {
              practitionerTouch.current = false;
              setSelectedPractitioners([]);
              setPatient(next);
            }}
          />
        ) : null}

        {phase.type === 'template' ? (
          <div className="space-y-s3">
            {templatesLoading ? (
              <>
                <h3 className={cn(TYPE.section, 'text-ds-text')}>
                  {t('treatment_courses.book_choose_course')}
                </h3>
                <p className={cn(TYPE.small, 'text-ds-muted')}>{t('common.loading')}</p>
              </>
            ) : activeTemplates.length === 0 ? (
              <>
                <h3 className={cn(TYPE.section, 'text-ds-text')}>
                  {t('treatment_courses.book_choose_course')}
                </h3>
                <p className={cn(TYPE.small, 'text-ds-muted')}>
                  {t('treatment_courses.book_no_templates')}
                </p>
              </>
            ) : (
              <CourseTemplateTypeahead
                courses={activeTemplates.flatMap((row) =>
                  row.id
                    ? [
                        {
                          id: row.id,
                          name: row.name,
                          detail: courseChoiceDetail(row, t),
                        },
                      ]
                    : [],
                )}
                value={template?.id ?? null}
                recentIds={recentIds}
                onChange={(id) => {
                  if (!id) {
                    selectTemplate(null);
                    return;
                  }
                  const next = activeTemplates.find((row) => row.id === id) ?? null;
                  if (next) selectTemplate(next);
                }}
                label={t('treatment_courses.book_choose_course')}
                placeholder={t('treatment_courses.book_course_search_placeholder')}
                changeLabel={t('treatment_courses.book_change_course')}
                emptyLabel={t('treatment_courses.book_course_search_empty')}
                recentLabel={t('treatment_courses.book_recent_courses')}
                availableLabel={t('treatment_courses.book_available_courses')}
              />
            )}
          </div>
        ) : null}

        {phase.type === 'step' && currentStep && currentBooking ? (
          <div className="space-y-s4">
            {canLeaveStepUnbooked(template?.booking_timing ?? 'all_upfront', phase.index) ? (
              <Checkbox
                checked={currentBooking.unbooked}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setBookings((rows) =>
                    rows.map((row, index) =>
                      index === phase.index
                        ? checked
                          ? markStepUnbooked(row)
                          : { ...row, unbooked: false }
                        : row,
                    ),
                  );
                }}
                label={t('treatment_courses.book_leave_unbooked')}
              />
            ) : null}

            {!currentBooking.unbooked ? (
              <>
                {phase.index > 0 && !previousBooking?.startsAt ? (
                  <Banner tone="info" title={t('treatment_courses.book_need_previous')} />
                ) : null}

                <PractitionerTypeahead
                  people={practitionerPeople}
                  selectedIds={selectedPractitioners}
                  onChange={choosePractitioners}
                  legend={t('treatment_courses.field_practitioners')}
                  searchPlaceholder={t('treatment_courses.book_practitioners_search')}
                  emptyLabel={t('treatment_courses.book_practitioners_search_empty')}
                  hint={t('treatment_courses.book_practitioners_hint')}
                />

                <div>
                  <div className="mb-s2 flex items-center justify-between gap-s2">
                    <span className="type-label text-ds-text">
                      {t('appointments.modal.datetime')}
                    </span>
                    <Button
                      type="button"
                      variant="link"
                      aria-expanded={showSlots}
                      data-testid="course-step-show-slots"
                      onClick={() => setShowSlots((open) => !open)}
                    >
                      {showSlots
                        ? t('appointments.modal.manual_entry')
                        : t('appointments.modal.show_availability')}
                    </Button>
                  </div>

                  {showSlots ? (
                    <AvailabilityPicker
                      clinicId={clinicId}
                      serviceId={currentStep.service_id}
                      members={availabilityMembers}
                      selectedClinicianId={
                        selectedPractitioners.length === 1 ? selectedPractitioners[0] : null
                      }
                      selectedDateTime={selectedSlotTime}
                      duration={availabilityDuration}
                      onSelect={onAvailabilitySelect}
                      onClinicianChange={(clinicianId) =>
                        choosePractitioners(clinicianId ? [clinicianId] : [])
                      }
                    />
                  ) : (
                    <div className="space-y-s2" data-testid="course-step-time">
                      <div className="grid grid-cols-1 gap-s3 nav:grid-cols-3">
                        <DanishDateField
                          name="course-step-date"
                          label={t('appointments.modal.date')}
                          value={draftCivil}
                          onChange={onDraftDate}
                          required
                          calendar
                        />
                        <TimeInput
                          id="course-step-start"
                          label={t('appointments.modal.starts_at')}
                          value={draftStart}
                          onChange={onDraftStart}
                          required
                          allowEmpty
                        />
                        <TimeInput
                          id="course-step-end"
                          label={t('appointments.modal.ends_at')}
                          value={draftEnd}
                          onChange={onDraftEnd}
                          required
                          allowEmpty
                        />
                      </div>
                      {stepDuration > 0 ? (
                        <p className={cn(TYPE.small, 'text-ds-muted')}>
                          {t('treatment_courses.book_duration_follows_step', {
                            minutes: stepDuration,
                          })}
                        </p>
                      ) : null}
                      {currentBooking.startsAt &&
                      currentBooking.endsAt &&
                      Date.parse(currentBooking.endsAt) <= Date.parse(currentBooking.startsAt) ? (
                        <p className={cn(TYPE.small, 'text-ds-danger')}>
                          {t('treatment_courses.book_end_before_start')}
                        </p>
                      ) : null}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <p className={cn(TYPE.small, 'text-ds-muted')}>
                {t('treatment_courses.book_unbooked_hint')}
              </p>
            )}
          </div>
        ) : null}

        {phase.type === 'review' && template ? (
          <div className="space-y-s3">
            <h3 className={cn(TYPE.section, 'text-ds-text')}>
              {t('treatment_courses.book_review')}
            </h3>
            <p className={cn(TYPE.small, 'text-ds-muted')}>
              {patient
                ? `${patient.first_name} ${patient.last_name}`.trim()
                : t('appointments.modal.patient')}
              {' · '}
              {template.name}
            </p>
            <ul className="space-y-s2">
              {template.steps.map((step, index) => {
                const booking = bookings[index];
                const name = stepDisplayName(
                  step,
                  serviceNames.get(step.service_id),
                  t('treatment_courses.step_no_service'),
                );
                return (
                  <li key={step.clientId} className="rounded-lg border border-ds-line px-s4 py-s3">
                    <p className={cn(TYPE.card, 'text-ds-text')}>
                      {t('treatment_courses.step_n', { n: index + 1 })}: {name}
                    </p>
                    {booking?.unbooked || !booking?.startsAt ? (
                      <p className={cn('mt-0.5', TYPE.small, 'text-ds-muted')}>
                        {t('treatment_courses.book_not_booked')}
                      </p>
                    ) : (
                      <p className={cn('mt-0.5', TYPE.small, 'text-ds-muted')}>
                        {new Intl.DateTimeFormat(
                          i18n.language.startsWith('da') ? 'da-DK' : 'en-GB',
                          { day: 'numeric', month: 'short', year: 'numeric' },
                        ).format(new Date(booking.startsAt))}
                        {' · '}
                        {formatAppointmentClock(new Date(booking.startsAt), i18n.language)}
                        {' · '}
                        {formatNames(
                          booking.practitionerIds,
                          memberNames,
                          t('appointments.unassigned'),
                        )}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </div>
    </SlideOver>
  );
}

function CoursePatientPicker({
  clinicId,
  value,
  onChange,
}: {
  clinicId: string;
  value: PatientPickerItem | null;
  onChange: (next: PatientPickerItem | null) => void;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const { data: results } = usePatientPicker(clinicId, search);
  const list = useMemo(() => results ?? [], [results]);
  const displayName = value ? `${value.first_name} ${value.last_name}`.trim() || value.id : '';
  const phone = value
    ? formatPatientTelephoneNumbers(value.telephone_subscribers, value.phone)
    : '';

  useEffect(() => {
    function onClickOutside(event: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  if (value) {
    return (
      <Field label={t('appointments.modal.patient')} required htmlFor="course-patient">
        <div className="flex items-center gap-s3 rounded-pop border border-ds-line bg-ds-bg px-s3 py-s2">
          <Avatar name={displayName} size={36} />
          <div className="min-w-0 flex-1">
            <p className={cn(TYPE.section, 'truncate text-ds-text')}>{displayName}</p>
            {phone ? (
              <p className={cn(TYPE.small, 'mt-0.5 truncate text-ds-muted')}>{phone}</p>
            ) : null}
          </div>
          <Button
            type="button"
            variant="tertiary"
            size="sm"
            onClick={() => {
              onChange(null);
              setSearch('');
            }}
          >
            {t('appointments.modal.change_patient')}
          </Button>
        </div>
      </Field>
    );
  }

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        id="course-patient"
        ref={searchRef as Ref<HTMLInputElement>}
        label={t('appointments.modal.patient')}
        required
        leadingIcon={<Search className="h-4 w-4" />}
        value={search}
        autoComplete="off"
        onChange={(event) => {
          setSearch(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={t('appointments.modal.patient_search_placeholder')}
      />
      {open && list.length > 0 ? (
        <ul className="absolute z-10 mt-s1 max-h-60 w-full overflow-auto rounded-pop border border-ds-line bg-ds-card py-s1 shadow-pop">
          {list.map((row) => {
            const name = `${row.first_name} ${row.last_name}`.trim() || row.id;
            return (
              <li key={row.id}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(row);
                    setSearch('');
                    setOpen(false);
                  }}
                  className="flex w-full items-center gap-s3 px-s3 py-s2 text-left hover:bg-ds-teal-tint"
                >
                  <Avatar name={name} size={36} />
                  <span className={cn(TYPE.section, 'block truncate text-ds-text')}>{name}</span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
